import { Capacitor } from '@capacitor/core'

/**
 * Friendly "denied" handling shared by every permission-gated feature.
 *
 * A hard OS denial can only be undone in Settings (iOS never re-prompts, Android
 * stops prompting after two denials), so every denied state must say so plainly
 * and offer an Open Settings button — see `PermissionDeniedNotice`.
 */
export type DeniedPermissionKind =
  | 'photos'
  | 'camera'
  | 'microphone'
  | 'location'
  | 'notifications'
  | 'contacts'

export type PermissionPlatform = 'ios' | 'android' | 'web'

export type PermissionDeniedCopy = {
  /** Short heading, e.g. "Photos access is off". */
  title: string
  /** Full explanation + what to do, shown in the alert. */
  body: string
}

const FEATURE: Record<DeniedPermissionKind, { noun: string; settingsLabel: string; purpose: string }> = {
  photos: { noun: 'photos', settingsLabel: 'Photos', purpose: 'attach images' },
  camera: { noun: 'camera', settingsLabel: 'Camera', purpose: 'take photos and make video calls' },
  microphone: {
    noun: 'microphone',
    settingsLabel: 'Microphone',
    purpose: 'talk on calls and record voice notes',
  },
  location: {
    noun: 'location',
    settingsLabel: 'Location',
    purpose: 'record your arrival and share your travel progress',
  },
  notifications: {
    noun: 'notifications',
    settingsLabel: 'Notifications',
    purpose: 'get messages and incoming calls when the app is closed',
  },
  contacts: { noun: 'contacts', settingsLabel: 'Contacts', purpose: 'find people from your contacts' },
}

export function currentPermissionPlatform(): PermissionPlatform {
  if (!Capacitor.isNativePlatform()) return 'web'
  return Capacitor.getPlatform() === 'android' ? 'android' : 'ios'
}

/** Clear, platform-aware copy for a denied permission. */
export function permissionDeniedCopy(
  kind: DeniedPermissionKind,
  platform: PermissionPlatform = currentPermissionPlatform(),
): PermissionDeniedCopy {
  const { noun, settingsLabel, purpose } = FEATURE[kind]
  const title = kind === 'notifications' ? 'Notifications are off' : `${settingsLabel} access is off`
  if (kind === 'notifications') {
    return {
      title,
      body:
        platform === 'web'
          ? 'Ascend can’t show notifications. Allow Notifications for this site in your browser’s site settings to ' +
            `${purpose}.`
          : `Ascend can’t send notifications. Turn on Notifications in Settings to ${purpose}.`,
    }
  }
  if (platform === 'web') {
    return {
      title,
      body:
        `Ascend can’t access your ${noun}. Allow ${settingsLabel} for this site in your browser’s ` +
        `site settings (the lock icon in the address bar) to ${purpose}.`,
    }
  }
  return {
    title,
    body: `Ascend can’t access your ${noun}. Turn on ${settingsLabel} access in Settings to ${purpose}.`,
  }
}

/** One-line variant for places that only have room for a string (e.g. call errors). */
export function permissionDeniedMessage(
  kind: DeniedPermissionKind,
  platform: PermissionPlatform = currentPermissionPlatform(),
): string {
  return permissionDeniedCopy(kind, platform).body
}

const DENIED_PATTERNS: Array<{ kind: DeniedPermissionKind; test: RegExp }> = [
  { kind: 'photos', test: /denied access to photos|photo(s)? (library )?(access|permission)/i },
  { kind: 'camera', test: /denied access to camera|camera (access|permission)/i },
  {
    kind: 'microphone',
    test: /denied access to (the )?(microphone|mic)|microphone (access|permission)/i,
  },
  { kind: 'location', test: /location (services? )?(permission|access)|denied access to location/i },
]

/**
 * Map a thrown plugin / browser error to the permission it complains about.
 * Returns null for anything that isn't a permission denial (cancel, hardware,
 * decode errors …) so callers keep their normal error handling.
 */
export function classifyPermissionDenial(
  err: unknown,
  /** Used for generic browser denials (NotAllowedError) that don't name a device. */
  fallback: DeniedPermissionKind | null = null,
): DeniedPermissionKind | null {
  const message = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  const name = err instanceof Error ? err.name : ''
  for (const { kind, test } of DENIED_PATTERNS) {
    if (test.test(message)) return kind
  }
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || /permission denied/i.test(message)) {
    // Browser/WebView denial — the caller knows which device it asked for.
    return fallback
  }
  return null
}

/** Thrown by native photo helpers so the UI can show the denied state, not raw text. */
export class PermissionDeniedError extends Error {
  readonly kind: DeniedPermissionKind

  constructor(kind: DeniedPermissionKind, cause?: unknown) {
    super(permissionDeniedMessage(kind))
    this.name = 'PermissionDeniedError'
    this.kind = kind
    if (cause !== undefined) (this as { cause?: unknown }).cause = cause
  }
}
